import React, { useContext } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import {
  GPUInstanceType,
  Location,
} from "types/InstanceTypes";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { isBrevCloud } from "utils/instanceTypes";
import { ComputeOverview } from "./ComputeOverview";

interface LaunchableComputeOverviewProps {
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceTypeObject: GPUInstanceType;
  onSetNewCurrentInstanceType?: (
    instanceTypeObject: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  diskStorage: string | null;
  cloudProviderContent?: React.ReactNode;
  onViewAllOptions?: () => void;
  onStorageChange?: (storage: string) => void;
  onStorageValidityChange?: (isValid: boolean) => void;
  availableLocations?: Location[];
  selectedLocation?: string | null;
  onLocationChange?: (location: string | null) => void;
}

export const LaunchableComputeOverview: React.FC<
  LaunchableComputeOverviewProps
> = ({
  intendedInstanceType,
  intendedDiskStorage,
  instanceTypeObject,
  onSetNewCurrentInstanceType,
  diskStorage,
  cloudProviderContent,
  onViewAllOptions,
  onStorageChange,
  onStorageValidityChange,
  availableLocations,
  selectedLocation,
  onLocationChange,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const isBrevCloudNodeInstance = isBrevCloud(instanceTypeObject.type);

  const warningSection =
    intendedInstanceType !== instanceTypeObject.type &&
    onSetNewCurrentInstanceType ? (
      <div className="mt-2 px-3 pb-3">
        <div className="flex flex-row items-center text-red-500">
          <Tooltip
            placement="top"
            title="You have swapped to an instance different from what the author originally configured. This could result in unexpected behavior."
          >
            <div className="flex flex-row items-center">
              <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
              <span className="text-xs text-red-500">Instance Changed</span>
            </div>
          </Tooltip>
          <Tooltip title="Revert to the original instance" placement="top">
            <ArrowUturnLeftIcon
              className="h-4 w-4 ml-2 cursor-pointer text-highlight hover:text-highlightLighter"
              onClick={() => {
                const foundInstanceType =
                  instanceTypesContext.allInstancesAvailable.find(
                    (instanceType) =>
                      instanceType.type === intendedInstanceType,
                  );
                if (foundInstanceType) {
                  onSetNewCurrentInstanceType(
                    foundInstanceType,
                    intendedDiskStorage || "",
                  );
                } else {
                  onSetNewCurrentInstanceType(null, intendedDiskStorage || "");
                }
              }}
            />
          </Tooltip>
        </div>
      </div>
    ) : null;

  return (
    <ComputeOverview
      instanceTypeObject={instanceTypeObject}
      diskStorage={diskStorage}
      warningSection={warningSection}
      showCloudProvider={!isBrevCloudNodeInstance}
      cloudProviderContent={cloudProviderContent}
      intendedDiskStorage={intendedDiskStorage}
      onViewAllOptions={onViewAllOptions}
      onStorageChange={onStorageChange}
      onStorageValidityChange={onStorageValidityChange}
      availableLocations={availableLocations}
      selectedLocation={selectedLocation}
      onLocationChange={onLocationChange}
    />
  );
};
